import { Collapse, Snackbar, Stack, Typography } from '@mui/material';
import { testPrompt } from 'API/linkedin-funnel';
import { getAllFunnelContacts } from 'API/linkedin-funnel-search';
import { ErrorMessages, LinkedinTypes, ProductGroups } from 'enums';
import { CanSendViaSalesNav, FunnelContact, FunnelFunctions, FunnelTypes } from 'features/co-pilot/co-pilot.types';
import { LinkedinAccount } from 'features/co-pilot/linkedin-accounts/linkedin-accounts.types';
import PaiDotLoader from 'features/ui/base-components/pai-dot-loader';
import PaiHelpIcon from 'features/ui/base-components/pai-help-icon';
import { OptionType } from 'features/ui/base-components/pai-auto-complete';
import PaiButton from 'features/ui/base-components/pai-button';
import PaiCheckbox from 'features/ui/base-components/pai-checkbox';
import PaiPlaceholders from 'features/ui/base-components/pai-placeholder';
import PaiRadioItem from 'features/ui/base-components/pai-radio-item';
import PaiTextArea from 'features/ui/base-components/pai-text-area';
import PaiTextField from 'features/ui/base-components/pai-text-field';
import _ from 'lodash';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Node, useReactFlow, useUpdateNodeInternals } from 'reactflow';
import useCampaignNodeStore from 'stores/campaign-node-store';
import useErrorStore from 'stores/error-store';
import { makeStyles } from 'tss-react/mui';
import { ContactFilter } from '../../campaign-builder.types';
import TestPromptDialog from './test-pompt-dialog';

interface Props {
  productGroup: ProductGroups;
  isSelected: boolean;
  campaignId: string;
  node: Node | null;
  loading: boolean;
  linkedinAccount: LinkedinAccount | undefined;
  customPlaceholders: import('../../campaign-builder.types').CustomPlaceholder[];
  fieldInUse: { [key: string]: boolean };

  handleOpenUnableToGenerateContactRequestDialog(): void;
}

const PromptManager: React.FC<Props> = ({
  productGroup,
  isSelected,
  campaignId,
  node,
  loading,
  linkedinAccount,
  customPlaceholders,
  fieldInUse,
  handleOpenUnableToGenerateContactRequestDialog,
}) => {
  const { classes, cx } = useStyles();
  const { t } = useTranslation();

  const funnelType: FunnelTypes | undefined =
    productGroup === ProductGroups.HR_BOOSTER
      ? FunnelTypes.ACTIVE_SOURCING_CO_PILOT
      : productGroup === ProductGroups.LEAD_BOOSTER
      ? FunnelTypes.SOCIAL_SELLING_CO_PILOT
      : undefined;

  const handleOpenErrorDialog = useErrorStore((state) => state.handleOpenErrorDialog);
  const setIsPromptChanging = useCampaignNodeStore((state) => state.setIsPromptChanging);

  const { id = '', data = { functionName: '', prompt: '' } } = node || {};
  const {
    functionName,
    prompt: functionPrompt,
    sendWithoutText: sendConnectionRequestWithoutText,
    useSalesNav,
    // For SEND_CALL_TRANSCRIPT, your backend might also return:
    emailAddress: initialEmail = '',
    numReminders: initialReminders = '1',
  } = data;

  const [prompt, setPrompt] = useState('');
  const [resultText, setResultText] = useState('');
  const [resultSubject, setResultSubject] = useState('');
  const [suggestedPrompt, setSuggestedPrompt] = useState('');
  const [guidanceUsed, setGuidanceUsed] = useState(false);

  const [sendWithoutText, setSendWithoutText] = useState(false);
  const [selectedLinkedinProduct, setSelectedLinkedinProduct] = useState(LinkedinTypes.LINKEDIN);
  const [addSignature, setAddSignature] = useState(false);

  const [emailAddress, setEmailAddress] = useState('');
  const [numReminders, setNumReminders] = useState('1');

  const [profilePlaceholderMissing, setProfilePlaceholderMissing] = useState(false);
  const [duplicateProfilePlaceholder, setDuplicateProfilePlaceholder] = useState(false);
  const [duplicateJobOfferPlaceholder, setDuplicateJobOfferPlaceholder] = useState(false);
  const [unsupportedPlaceholders, setUnsupportedPlaceholders] = useState<string[]>([]);

  const [copiedSnackbarOpen, setCopiedSnackbarOpen] = useState(false);
  const [suggestionSnackbarMessage, setSuggestionSnackbarMessage] = useState('');
  const [suggestionSnackbarOpen, setSuggestionSnackbarOpen] = useState(false);

  const [testPromptDialog, setTestPromptDialog] = useState<{
    open: boolean;
    loading: boolean;
    contacts: FunnelContact[];
    contactsLoading: boolean;
    selectedContact: string;
  }>({
    open: false,
    loading: false,
    contacts: [],
    contactsLoading: false,
    selectedContact: 'default',
  });

  const products = [
    { label: 'LinkedIn', value: LinkedinTypes.LINKEDIN },
    { label: 'Sales Navigator', value: LinkedinTypes.SALES_NAV },
  ];

  const typingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const controllersRef = useRef(new Map<string, AbortController>());

  const createAbortController = (key: string) => {
    if (controllersRef.current.has(key)) {
      controllersRef.current.get(key)?.abort();
    }

    const controller = new AbortController();
    controllersRef.current.set(key, controller);
    return controller;
  };

  const clearAbortControllers = () => {
    controllersRef.current.forEach((controller) => controller.abort());
    controllersRef.current.clear();
  };

  // Initialize states when node changes
  useEffect(() => {
    setPrompt(functionPrompt || '');
    setSendWithoutText(!!sendConnectionRequestWithoutText);
    setSelectedLinkedinProduct(useSalesNav ? LinkedinTypes.SALES_NAV : LinkedinTypes.LINKEDIN);
    setAddSignature(!!data.enableSignature);

    // If the node is SEND_CALL_TRANSCRIPT, fill local states from data
    if (functionName === FunnelFunctions.SEND_CALL_TRANSCRIPT) {
      setEmailAddress(initialEmail || '');
      setNumReminders(initialReminders || '1');
    }
  }, [id, data]);

  const { setNodes } = useReactFlow();
  const updateNodeInternals = useUpdateNodeInternals();

  const handlePromptChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    /* debug logs removed */
    setPrompt(event.target.value);

    // User started editing – show "waiting" immediately if not already active
    setIsPromptChanging(true);
  };

  const handleKeyUp = () => {
    /* debug logs removed */
    if (typingTimerRef.current) clearTimeout(typingTimerRef.current);

    typingTimerRef.current = setTimeout(() => {
      handleSavePrompt();
      setIsPromptChanging(false);
    }, 1000);
  };

  const handleKeyDown = () => {
    /* debug logs removed */
    if (typingTimerRef.current) clearTimeout(typingTimerRef.current);
    setIsPromptChanging(true);
  };

  const handleEmailAddressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmailAddress(e.target.value);
  };

  const handleNumRemindersChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (typingTimerRef.current) clearTimeout(typingTimerRef.current);
    setIsPromptChanging(true);

    const newValue = event.target.value;
    setNumReminders(newValue);

    typingTimerRef.current = setTimeout(() => {
      handleSavePrompt(newValue);
      setIsPromptChanging(false);
    }, 1 * 1000);
  };

  /** SAVE the changes back to the node data */
  const handleSavePrompt = (newNumReminders: string = numReminders) => {
    // debug logs removed
    setNodes((nds) => {
      const updated = _.cloneDeep(nds);
      updated.forEach((nd) => {
        if (nd.id === id) {
          nd.data.prompt = prompt; // always save the prompt

          // Save "sendWithoutText" if this function uses it
          if (functionName === FunnelFunctions.SEND_CONNECTION_REQUEST) {
            nd.data.sendWithoutText = sendWithoutText;
          }

          // Save useSalesNav if the function is in the "CanSendViaSalesNav" list
          if (_.includes(CanSendViaSalesNav, functionName)) {
            nd.data.useSalesNav = selectedLinkedinProduct === LinkedinTypes.SALES_NAV;
          }

          // NEW: Save additional fields if it's SEND_CALL_TRANSCRIPT
          if (functionName === FunnelFunctions.SEND_CALL_TRANSCRIPT) {
            nd.data.emailAddress = emailAddress;
            nd.data.numReminders = newNumReminders;
          }
        }
      });
      return updated;
    });

    updateNodeInternals(id);
  };

  /** TOGGLE: Send connection request without text */
  const handleSendWithoutTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSendWithoutText(event.target.checked);

    setNodes((nds) => {
      const updated = _.cloneDeep(nds);
      updated.forEach((nd) => {
        if (nd.id === id) {
          nd.data.sendWithoutText = event.target.checked;
        }
      });
      return updated;
    });

    updateNodeInternals(id);
  };

  /** SWITCH product between LinkedIn / Sales Nav (if the function supports it) */
  const handlePromptLiProductChange = (value: string) => {
    return () => {
      setSelectedLinkedinProduct(value as LinkedinTypes);
      setNodes((nds) => {
        const updated = _.cloneDeep(nds);
        updated.forEach((nd) => {
          if (nd.id === id && _.includes(CanSendViaSalesNav, functionName)) {
            nd.data.useSalesNav = (value as LinkedinTypes) === LinkedinTypes.SALES_NAV;
          }
        });
        return updated;
      });
      updateNodeInternals(id);
    };
  };

  const handleAddSignatureChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const enableSignature = event.target.checked;
    setAddSignature(enableSignature);

    // Update the node's enableSignature property
    setNodes((nds) => {
      const updated = _.cloneDeep(nds);
      updated.forEach((nd) => {
        if (nd.id === id) {
          nd.data.enableSignature = enableSignature;
        }
      });
      return updated;
    });

    updateNodeInternals(id);
  };

  /**
   * Placeholder handlers
   */

  enum Placeholders {
    JOB_OFFER = '[[{{jobOffer}}]]',
    PROFILE = '[[{{profile}}]]',
    MESSAGE_CONTEXT = '[[{{messageContext}}]]',
    PROBLEMS = '[[{{problems}}]]',
    LANG = '{{lang}}',
    FEATURES = '{{features}}',
    BENEFITS = '{{benefits}}',
    IMPACT = '{{impact}}',
    JOB_TITLE = '{{jobTitle}}',
  }

  const recruitingPlaceholders = {
    [FunnelFunctions.SEND_CONNECTION_REQUEST]: [
      { label: t('jobOffer'), value: Placeholders.JOB_OFFER },
      { label: t('jobTitle'), value: Placeholders.JOB_TITLE },
      { label: t('language'), value: Placeholders.LANG },
      { label: t('profile'), value: Placeholders.PROFILE },
    ],
    [FunnelFunctions.SEND_MAIL]: [
      { label: t('jobOffer'), value: Placeholders.JOB_OFFER },
      { label: t('jobTitle'), value: Placeholders.JOB_TITLE },
      { label: t('language'), value: Placeholders.LANG },
      { label: t('profile'), value: Placeholders.PROFILE },
    ],
    [FunnelFunctions.SEND_MAIL_WITH_CONTEXT]: [
      { label: t('jobOffer'), value: Placeholders.JOB_OFFER },
      { label: t('jobTitle'), value: Placeholders.JOB_TITLE },
      { label: t('language'), value: Placeholders.LANG },
      { label: t('profile'), value: Placeholders.PROFILE },
      { label: t('messageContext'), value: Placeholders.MESSAGE_CONTEXT },
    ],
    [FunnelFunctions.SEND_IN_MAIL]: [
      { label: t('jobOffer'), value: Placeholders.JOB_OFFER },
      { label: t('jobTitle'), value: Placeholders.JOB_TITLE },
      { label: t('language'), value: Placeholders.LANG },
      { label: t('profile'), value: Placeholders.PROFILE },
    ],
    [FunnelFunctions.SEND_IN_MAIL_WITH_CONTEXT]: [
      { label: t('jobOffer'), value: Placeholders.JOB_OFFER },
      { label: t('jobTitle'), value: Placeholders.JOB_TITLE },
      { label: t('language'), value: Placeholders.LANG },
      { label: t('profile'), value: Placeholders.PROFILE },
      { label: t('messageContext'), value: Placeholders.MESSAGE_CONTEXT },
    ],
    [FunnelFunctions.SEND_EMAIL]: [
      { label: t('jobOffer'), value: Placeholders.JOB_OFFER },
      { label: t('jobTitle'), value: Placeholders.JOB_TITLE },
      { label: t('language'), value: Placeholders.LANG },
      { label: t('profile'), value: Placeholders.PROFILE },
    ],
  };

  const leadPlaceholders = {
    [FunnelFunctions.SEND_CONNECTION_REQUEST]: [
      { label: t('problems'), value: Placeholders.PROBLEMS },
      { label: t('language'), value: Placeholders.LANG },
      { label: t('profile'), value: Placeholders.PROFILE },
    ],
    [FunnelFunctions.SEND_MAIL]: [
      { label: t('problems'), value: Placeholders.PROBLEMS },
      { label: t('language'), value: Placeholders.LANG },
      { label: t('profile'), value: Placeholders.PROFILE },
    ],
    [FunnelFunctions.SEND_MAIL_WITH_CONTEXT]: [
      { label: t('problems'), value: Placeholders.PROBLEMS },
      { label: t('language'), value: Placeholders.LANG },
      { label: t('profile'), value: Placeholders.PROFILE },
      { label: t('messageContext'), value: Placeholders.MESSAGE_CONTEXT },
    ],
    [FunnelFunctions.SEND_IN_MAIL]: [
      { label: t('problems'), value: Placeholders.PROBLEMS },
      { label: t('language'), value: Placeholders.LANG },
      { label: t('profile'), value: Placeholders.PROFILE },
    ],
    [FunnelFunctions.SEND_IN_MAIL_WITH_CONTEXT]: [
      { label: t('problems'), value: Placeholders.PROBLEMS },
      { label: t('language'), value: Placeholders.LANG },
      { label: t('profile'), value: Placeholders.PROFILE },
      { label: t('messageContext'), value: Placeholders.MESSAGE_CONTEXT },
    ],
    [FunnelFunctions.SEND_FOLLOW_UP]: [
      { label: t('problems'), value: Placeholders.PROBLEMS },
      { label: t('features'), value: Placeholders.FEATURES },
      { label: t('benefits'), value: Placeholders.BENEFITS },
      { label: t('impact'), value: Placeholders.IMPACT },
      { label: t('messageContext'), value: Placeholders.MESSAGE_CONTEXT },
      { label: t('language'), value: Placeholders.LANG },
      { label: t('profile'), value: Placeholders.PROFILE },
    ],
    [FunnelFunctions.SEND_EMAIL]: [
      { label: t('problems'), value: Placeholders.PROBLEMS },
      { label: t('language'), value: Placeholders.LANG },
      { label: t('profile'), value: Placeholders.PROFILE },
    ],
    [FunnelFunctions.SEND_FOLLOW_UP_EMAIL]: [
      { label: t('problems'), value: Placeholders.PROBLEMS },
      { label: t('features'), value: Placeholders.FEATURES },
      { label: t('benefits'), value: Placeholders.BENEFITS },
      { label: t('impact'), value: Placeholders.IMPACT },
      { label: t('messageContext'), value: Placeholders.MESSAGE_CONTEXT },
      { label: t('language'), value: Placeholders.LANG },
      { label: t('profile'), value: Placeholders.PROFILE },
    ],
    [FunnelFunctions.SEND_CALL_TRANSCRIPT]: [
      { label: t('profile'), value: Placeholders.PROFILE },
      { label: t('problems'), value: Placeholders.PROBLEMS },
      { label: t('features'), value: Placeholders.FEATURES },
      { label: t('benefits'), value: Placeholders.BENEFITS },
      { label: t('impact'), value: Placeholders.IMPACT },
    ],
  };

  const getOptions = (productGroup: ProductGroups, functionName: FunnelFunctions): OptionType[] => {
    type RecruitingKeys = keyof typeof recruitingPlaceholders;
    type LeadKeys = keyof typeof leadPlaceholders;

    if (productGroup === ProductGroups.HR_BOOSTER && functionName in recruitingPlaceholders) {
      return recruitingPlaceholders[functionName as RecruitingKeys];
    } else if (productGroup === ProductGroups.LEAD_BOOSTER && functionName in leadPlaceholders) {
      return leadPlaceholders[functionName as LeadKeys];
    }

    return [];
  };

  const options = useMemo(() => getOptions(productGroup, functionName), [productGroup, functionName]);

  /**
   * Placeholder checks
   */

  const missingPlaceholders = useMemo(() => {
    return funnelType === FunnelTypes.ACTIVE_SOURCING_CO_PILOT
      ? checkActiveSourcingPlaceholders()
      : funnelType === FunnelTypes.SOCIAL_SELLING_CO_PILOT
      ? checkSocialSellingPlaceholders()
      : [];
  }, [prompt, customPlaceholders, fieldInUse]);

  /** Custom placeholders that are defined but not used in the current prompt */
  const missingCustomPlaceholders = useMemo(() => {
    if (!customPlaceholders || customPlaceholders.length === 0) return [] as string[];

    const missing: string[] = [];
    customPlaceholders.forEach((cp) => {
      const token = `[[{{${cp.key}}}]]`;
      if (!prompt.includes(token)) {
        missing.push(token);
      }
    });
    return missing;
  }, [prompt, customPlaceholders]);

  useEffect(() => {
    checkDuplicatePlaceholders();
    checkUnsupportedPlaceholders();
  }, [prompt, customPlaceholders]);

  function checkActiveSourcingPlaceholders() {
    const missingPlaceholders: string[] = [];

    if (!prompt.includes('{{profile}}')) {
      setProfilePlaceholderMissing(true);
    } else {
      setProfilePlaceholderMissing(false);
    }

    if (functionName === FunnelFunctions.SEND_CALL_TRANSCRIPT) {
      return missingPlaceholders;
    }

    if (fieldInUse?.jobOffer && !prompt.includes('{{jobOffer}}')) {
      missingPlaceholders.push('{{jobOffer}}');
    }
    if (
      functionName === FunnelFunctions.SEND_MAIL_WITH_CONTEXT ||
      functionName === FunnelFunctions.SEND_IN_MAIL_WITH_CONTEXT ||
      functionName === FunnelFunctions.SEND_EMAIL_WITH_CONTEXT
    ) {
      if (!prompt.includes('{{messageContext}}')) {
        missingPlaceholders.push('{{messageContext}}');
      }
    }
    if (!prompt.includes('{{lang}}')) {
      missingPlaceholders.push('{{lang}}');
    }

    return missingPlaceholders;
  }

  function checkSocialSellingPlaceholders() {
    const missingPlaceholders: string[] = [];

    if (!prompt.includes('{{profile}}')) {
      setProfilePlaceholderMissing(true);
    } else {
      setProfilePlaceholderMissing(false);
    }
    if (fieldInUse?.problems && !prompt.includes('{{problems}}')) {
      missingPlaceholders.push('{{problems}}');
    }

    if (functionName === FunnelFunctions.SEND_CALL_TRANSCRIPT) {
      if (fieldInUse?.features && !prompt.includes('{{features}}')) {
        missingPlaceholders.push('{{features}}');
      }
      if (fieldInUse?.benefits && !prompt.includes('{{benefits}}')) {
        missingPlaceholders.push('{{benefits}}');
      }
      if (fieldInUse?.impact && !prompt.includes('{{impact}}')) {
        missingPlaceholders.push('{{impact}}');
      }
    }

    if (functionName === FunnelFunctions.SEND_FOLLOW_UP || functionName === FunnelFunctions.SEND_FOLLOW_UP_EMAIL) {
      if (fieldInUse?.features && !prompt.includes('{{features}}')) {
        missingPlaceholders.push('{{features}}');
      }
      if (fieldInUse?.benefits && !prompt.includes('{{benefits}}')) {
        missingPlaceholders.push('{{benefits}}');
      }
      if (fieldInUse?.impact && !prompt.includes('{{impact}}')) {
        missingPlaceholders.push('{{impact}}');
      }
      if (!prompt.includes('{{messageContext}}')) {
        missingPlaceholders.push('{{messageContext}}');
      }
    }

    if (
      functionName === FunnelFunctions.SEND_MAIL_WITH_CONTEXT ||
      functionName === FunnelFunctions.SEND_IN_MAIL_WITH_CONTEXT ||
      functionName === FunnelFunctions.SEND_EMAIL_WITH_CONTEXT
    ) {
      if (!prompt.includes('{{messageContext}}')) {
        missingPlaceholders.push('{{messageContext}}');
      }
    }

    if (!prompt.includes('{{lang}}') && functionName !== FunnelFunctions.SEND_CALL_TRANSCRIPT) {
      missingPlaceholders.push('{{lang}}');
    }
    return missingPlaceholders;
  }

  function checkDuplicatePlaceholders() {
    const profileMatches = (prompt.match(/{{profile}}/g) || []).length;
    const jobOfferMatches = (prompt.match(/{{jobOffer}}/g) || []).length;

    setDuplicateProfilePlaceholder(profileMatches > 1);
    setDuplicateJobOfferPlaceholder(jobOfferMatches > 1);
  }

  function checkUnsupportedPlaceholders() {
    // Get all supported placeholders
    let supportedPlaceholders = Object.values(Placeholders).map((p) => p.replace(/[[\]]/g, ''));
    // Add custom placeholders from funnel
    if (customPlaceholders && customPlaceholders.length > 0) {
      const customTokens = customPlaceholders.map((cp) => `{{${cp.key}}}`);
      supportedPlaceholders = [...supportedPlaceholders, ...customTokens];
    }

    // Find all placeholders in the prompt that match the pattern {{text}}
    const placeholderRegex = /{{([^}]+)}}/g;
    const matches = prompt.match(placeholderRegex) || [];

    // Filter out supported placeholders
    const unsupported = matches.filter((match) => !supportedPlaceholders.includes(match));

    setUnsupportedPlaceholders(unsupported);
  }

  const handleCopyPlaceholder = (token: string) => {
    // Copy to clipboard
    navigator.clipboard.writeText(token).then(() => {
      setCopiedSnackbarOpen(true);
    });
  };

  const handleApplySuggestedPrompt = () => {
    if (!suggestedPrompt.trim()) return;

    setPrompt(suggestedPrompt);
    setNodes((nds) => {
      const updated = _.cloneDeep(nds);
      updated.forEach((nd) => {
        if (nd.id === id) {
          nd.data.prompt = suggestedPrompt;
        }
      });
      return updated;
    });
    updateNodeInternals(id);

    setSuggestionSnackbarMessage(t('suggestedPromptApplied'));
    setSuggestionSnackbarOpen(true);
  };

  const handleCopySuggestedPrompt = () => {
    if (!suggestedPrompt.trim()) return;
    navigator.clipboard.writeText(suggestedPrompt).then(() => {
      setSuggestionSnackbarMessage(t('suggestedPromptCopied'));
      setSuggestionSnackbarOpen(true);
    });
  };

  // Filter out placeholders that are technically unsupported so they are not shown as errors (red)
  const missingPlaceholdersToRender = useMemo(
    () => missingPlaceholders.filter((ph) => !unsupportedPlaceholders.includes(ph)),
    [missingPlaceholders, unsupportedPlaceholders]
  );

  const missingCustomPlaceholdersToRender = useMemo(
    () =>
      missingCustomPlaceholders.filter((ph) => {
        // Remove surrounding [[ and ]] when comparing with unsupported list
        const unwrapped = ph.replace(/^\[\[|\]\]$/g, '');
        return !unsupportedPlaceholders.includes(unwrapped);
      }),
    [missingCustomPlaceholders, unsupportedPlaceholders]
  );

  /**
   * Test Prompt Dialog
   */

  const handleOpenTestPromptDialog = async () => {
    setTestPromptDialog((prev) => ({ ...prev, open: true, contactsLoading: true }));

    const params = {
      filter: ContactFilter.PROCESSED,
      limit: 10,
      offset: 0,
    };
    const abortController = createAbortController('getAllFunnelContacts');
    const res = await getAllFunnelContacts(campaignId, params, abortController.signal);

    if (!res.error) {
      setTestPromptDialog((prev) => ({ ...prev, contacts: res.funnelContacts ?? [], contactsLoading: false }));
    } else {
      setTestPromptDialog((prev) => ({ ...prev, contactsLoading: false }));
    }
  };

  const handleCloseTestPromptDialog = () => {
    setTestPromptDialog({ open: false, loading: false, contacts: [], contactsLoading: false, selectedContact: 'default' });

    clearAbortControllers();
  };

  const handleSelectContact = (event: React.ChangeEvent<HTMLInputElement>) => {
    setTestPromptDialog((prev) => ({ ...prev, selectedContact: event.target.value }));
  };

  const handleTestPrompt = async () => {
    if (_.isNil(campaignId)) return;

    const { selectedContact } = testPromptDialog;

    setTestPromptDialog((prev) => ({ ...prev, loading: true }));
    setResultText('');
    setResultSubject('');
    setSuggestedPrompt('');
    setGuidanceUsed(false);

    const abortController = createAbortController('testPrompt');
    const res = await testPrompt(
      campaignId,
      functionName,
      prompt,
      addSignature,
      selectedContact === 'default' ? undefined : selectedContact ?? undefined,
      abortController.signal
    );

    if (!res.error) {
      setResultText(res.text);
      setResultSubject(res.subject);
      const returnedSuggestedPrompt = typeof res.suggestedPrompt === 'string' ? res.suggestedPrompt.trim() : '';
      const hasGuidance = Boolean(res.guidanceUsed) || returnedSuggestedPrompt.length > 0;
      setSuggestedPrompt(returnedSuggestedPrompt);
      setGuidanceUsed(hasGuidance);
      handleCloseTestPromptDialog();
    } else {
      if (res.error.message?.includes(ErrorMessages.UNABLE_TO_GENERATE_MESSAGE)) {
        handleOpenUnableToGenerateContactRequestDialog();
      } else {
        handleOpenErrorDialog(res?.error, `/campaigns/${campaignId}/testFunctionPrompt`, 'post');
      }
    }

    setTestPromptDialog((prev) => ({ ...prev, loading: false }));
  };

  useEffect(() => {
    return () => {
      clearAbortControllers();
    };
  }, []);

  return (
    <Collapse in={isSelected} unmountOnExit>
      <Stack className={classes.promptManager} gap={3}>
        {/* For a SEND_CONNECTION_REQUEST node, optionally toggle "sendWithoutText" */}
        {functionName === FunnelFunctions.SEND_CONNECTION_REQUEST && (
          <Stack gap={1}>
            <div className={classes.textContainer}>
              <Typography className={classes.formTitle}>{t('settings')}</Typography>
            </div>
            <div className={classes.backgroundContainer}>
              <PaiCheckbox checked={sendWithoutText} dense label={t('sendConnectionRequestWithoutText')} onChange={handleSendWithoutTextChange} />
            </div>
          </Stack>
        )}

        {/* If the node can send via Sales Nav, and the user's account supports it */}
        {_.includes(CanSendViaSalesNav, functionName) && !_.isNil(linkedinAccount) && linkedinAccount.liProductType === LinkedinTypes.SALES_NAV && (
          <Stack gap={1}>
            <div className={classes.textContainer}>
              <Typography className={classes.formTitle}>{t('settings')}</Typography>
            </div>
            <Stack className={classes.backgroundContainer} gap={1}>
              <Typography className={classes.formText}>{t('linkedinProduct')}</Typography>
              <Stack direction="row" gap={3}>
                {products.map((product) => (
                  <PaiRadioItem
                    key={product.value}
                    label={product.label}
                    value={product.value}
                    selectedValue={selectedLinkedinProduct}
                    variant="row"
                    handleSelectItem={handlePromptLiProductChange}
                  />
                ))}
              </Stack>
            </Stack>
          </Stack>
        )}

        {/* The main Prompt text area (shared by most function types) */}
        <Stack gap={1}>
          <Stack className={classes.textContainer} direction="row" gap={1}>
            <PaiHelpIcon tooltipText={t('promptHelpText')} />
            <Typography className={classes.helperText}>{t('whatIsPrompt')}</Typography>
          </Stack>
          <Stack className={classes.textContainer}>
            <Typography className={classes.formTitle}>{`Prompt | ${t(functionName)}`}</Typography>
          </Stack>
          <PaiTextArea
            value={prompt}
            rows={5}
            variant="background"
            headAction={<PaiPlaceholders options={options} />}
            onChange={handlePromptChange}
            onKeyDown={handleKeyDown}
            onKeyUp={handleKeyUp}
            disabled={testPromptDialog.loading || sendWithoutText}
            onBlur={() => {
              if (typingTimerRef.current) clearTimeout(typingTimerRef.current);
              handleSavePrompt();
              setIsPromptChanging(false);
            }}
          />
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            {functionName && functionName !== FunnelFunctions.SEND_CONNECTION_REQUEST ? (
              <PaiCheckbox checked={addSignature} onChange={handleAddSignatureChange} label={t('addSignatureToMessage')} />
            ) : (
              <div />
            )}
            <PaiButton
              text={t('testPrompt')}
              size="small"
              border="round"
              disabled={testPromptDialog.loading || sendWithoutText || prompt.trim() === ''}
              action={handleOpenTestPromptDialog}
            />
          </Stack>
        </Stack>

        {/* Critical warnings (red) */}
        {(profilePlaceholderMissing || missingPlaceholdersToRender.length > 0 || duplicateProfilePlaceholder || duplicateJobOfferPlaceholder) &&
        isSelected ? (
          <Stack className={classes.warningContainer} gap={1}>
            {profilePlaceholderMissing ? <div className={classes.placeholderWarning}>{t('profilePlaceholderMissingWarning')}</div> : null}
            {missingPlaceholdersToRender.length > 0 ? (
              <div className={classes.placeholderWarning}>
                {t('missingPlaceholdersWarningPrefix')}{' '}
                {missingPlaceholdersToRender.map((ph, idx) => (
                  <span
                    key={ph}
                    role="button"
                    tabIndex={0}
                    className={classes.copyToken}
                    onClick={() => handleCopyPlaceholder(ph)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') handleCopyPlaceholder(ph);
                    }}
                  >
                    {ph}
                    {idx < missingPlaceholdersToRender.length - 1 ? ', ' : ''}
                  </span>
                ))}
              </div>
            ) : null}
            {duplicateProfilePlaceholder ? <div className={classes.placeholderWarning}>{t('duplicateProfilePlaceholderWarning')}</div> : null}
            {duplicateJobOfferPlaceholder ? <div className={classes.placeholderWarning}>{t('duplicateJobOfferPlaceholderWarning')}</div> : null}
          </Stack>
        ) : null}

        {/* Unsupported placeholders (blue) */}
        {(unsupportedPlaceholders.length > 0 || missingCustomPlaceholdersToRender.length > 0) && isSelected ? (
          <Stack className={classes.infoContainer} gap={0.5}>
            <div className={classes.placeholderInfo}>{t('unsupportedPlaceholdersWarning', { placeholders: unsupportedPlaceholders.join(', ') })}</div>
            {missingCustomPlaceholdersToRender.length > 0 ? (
              <div className={classes.placeholderInfo}>
                {t('missingCustomPlaceholdersWarningPrefix')}{' '}
                {missingCustomPlaceholdersToRender.map((ph, idx) => (
                  <span
                    key={ph}
                    role="button"
                    tabIndex={0}
                    className={classes.copyToken}
                    onClick={() => handleCopyPlaceholder(ph)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') handleCopyPlaceholder(ph);
                    }}
                  >
                    {ph}
                    {idx < missingCustomPlaceholdersToRender.length - 1 ? ', ' : ''}
                  </span>
                ))}
              </div>
            ) : null}
          </Stack>
        ) : null}

        {/* Extra fields if the node is "SEND_CALL_TRANSCRIPT" */}
        {functionName === FunnelFunctions.SEND_CALL_TRANSCRIPT && (
          <Stack gap={2}>
            <Stack gap={1}>
              <Typography className={classes.formTitle}>{t('emailAddress')}</Typography>
              <PaiTextField
                value={emailAddress}
                onKeyDown={handleKeyDown}
                onKeyUp={handleKeyUp}
                onChange={handleEmailAddressChange}
                placeholder={t('enterEmailForTranscripts')}
              />
            </Stack>

            <Stack gap={1}>
              <Typography className={classes.formTitle}>{t('howManyReminders')}</Typography>
              <PaiTextField value={numReminders} type="number" onChange={handleNumRemindersChange} placeholder="Number of reminders to send" />
            </Stack>
          </Stack>
        )}

        {/* Display tested prompt results (if any) */}
        <Stack gap={1}>
          <Stack className={classes.textContainer}>
            <Typography className={classes.formTitle}>{`${t('generatedText')} | ${t(functionName)}`}</Typography>
            {resultSubject ? <Typography className={classes.formText}>{`Subject | ${resultSubject}`}</Typography> : null}
            {guidanceUsed ? <Typography className={classes.guidanceTag}>{t('promptGuidanceUsed')}</Typography> : null}
          </Stack>
          <div className={cx(classes.resultTextContainer, testPromptDialog.loading ? 'loading' : undefined)}>
            <span className={cx(classes.loader, testPromptDialog.loading ? 'loading' : undefined)}>
              <PaiDotLoader size="medium" />
            </span>
            <PaiTextArea value={resultText} rows={5} variant="background" readOnly disabled={loading || testPromptDialog.loading} />
          </div>
        </Stack>
        {guidanceUsed && suggestedPrompt ? (
          <Stack className={classes.guidanceContainer} gap={1}>
            <Typography className={classes.guidanceTitle}>{t('promptSuggestionTitle')}</Typography>
            <Typography className={classes.guidanceDescription}>{t('promptSuggestionDescription')}</Typography>
            <PaiTextArea value={suggestedPrompt} rows={6} variant="background" readOnly disabled={loading || testPromptDialog.loading} />
            <Stack direction="row" justifyContent="flex-end" gap={1}>
              <PaiButton
                text={t('copyPromptSuggestion')}
                size="small"
                border="round"
                action={handleCopySuggestedPrompt}
                disabled={testPromptDialog.loading}
              />
              <PaiButton
                text={t('applyPromptSuggestion')}
                size="small"
                border="round"
                action={handleApplySuggestedPrompt}
                disabled={testPromptDialog.loading}
              />
            </Stack>
          </Stack>
        ) : null}
      </Stack>

      <Snackbar
        open={copiedSnackbarOpen}
        message={t('placeholderCopied')}
        autoHideDuration={2000}
        onClose={() => setCopiedSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
      <Snackbar
        open={suggestionSnackbarOpen}
        message={suggestionSnackbarMessage}
        autoHideDuration={2200}
        onClose={() => setSuggestionSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
      <TestPromptDialog
        open={testPromptDialog.open}
        loading={testPromptDialog.loading}
        contacts={testPromptDialog.contacts}
        contactsLoading={testPromptDialog.contactsLoading}
        selectedContact={testPromptDialog.selectedContact}
        handleSelectContact={handleSelectContact}
        handleClose={handleCloseTestPromptDialog}
        handleTestPrompt={handleTestPrompt}
      />
    </Collapse>
  );
};

const useStyles = makeStyles()((theme) => ({
  promptManager: {
    padding: '16px',
    height: '100%',
    borderTop: `1px solid ${theme.palette.greys.light}`,
  },
  textContainer: {
    padding: '0 8px',
  },
  backgroundContainer: {
    padding: '8px',
    background: theme.palette.backgrounds.main_new,
    borderRadius: '10px',
  },
  formTitle: {
    fontSize: theme.typography.pxToRem(14),
    fontWeight: 'bold',
  },
  formText: {
    fontSize: theme.typography.pxToRem(14),
    color: theme.palette.greys.medium,
  },
  guidanceTag: {
    marginTop: '4px',
    fontSize: theme.typography.pxToRem(12),
    color: theme.palette.primary.main,
    fontWeight: 'bold',
  },
  helperText: {
    fontSize: theme.typography.pxToRem(14),
    textDecoration: 'underline',
  },
  resultTextContainer: {
    position: 'relative',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  guidanceContainer: {
    padding: '12px',
    border: `1px solid ${theme.palette.primary.main}`,
    borderRadius: '10px',
    background: theme.palette.backgrounds.main_new,
  },
  guidanceTitle: {
    fontSize: theme.typography.pxToRem(14),
    fontWeight: 'bold',
    color: theme.palette.primary.main,
  },
  guidanceDescription: {
    fontSize: theme.typography.pxToRem(13),
    color: theme.palette.greys.medium,
  },
  loader: {
    display: 'none',
    position: 'absolute',
    zIndex: 9,
    '&.loading': {
      display: 'flex',
    },
  },
  warningContainer: {
    padding: '8px 16px',
    border: `2px solid ${theme.palette.error.main}`,
    borderRadius: '8px',
  },
  infoContainer: {
    padding: '8px 16px',
    border: `2px solid ${theme.palette.primary.main}`,
    borderRadius: '8px',
  },
  placeholderWarning: {
    color: theme.palette.error.main,
    fontSize: theme.typography.pxToRem(13),
  },
  placeholderInfo: {
    color: theme.palette.primary.main,
    fontSize: theme.typography.pxToRem(13),
  },
  copyToken: {
    textDecoration: 'underline',
    cursor: 'pointer',
  },
}));

export default PromptManager;
